import { ReactNode, useRef, useState } from 'react';
import { Page } from 'react-pdf/dist/cjs';
import { OnPageLoadSuccess, OnRenderSuccess } from 'react-pdf/dist/cjs/shared/types';
import { Popup } from 'semantic-ui-react';
import { v4 as uuidv4 } from 'uuid';
import { ExtractionSpecType, HighlightSpecType } from '../types';
import { percentageToPixels, pixelsToPercentage } from '../utils/conversions';
import './styles.css';

const MIN_SELECTION_BOX_WIDTH_PX = 30;
const MIN_SELECTION_BOX_HEIGHT_PX = 30;

type PDFPagePropsType = {
    page: number;
    handleExtraction: (extractionSpec: ExtractionSpecType) => void;
    onLoadSuccess?: OnPageLoadSuccess;
    onRenderSuccess?: OnRenderSuccess;
    selectionDisabled: boolean;
    pendingPageExtractionSpecs: HighlightSpecType[];
    scale: number;
};
function PDFPage({
    page,
    handleExtraction,
    onLoadSuccess,
    pendingPageExtractionSpecs,
    selectionDisabled,
    onRenderSuccess,
    scale,
}: PDFPagePropsType) {
    const [startingPosition, setStartingPosition] = useState<{ x: number; y: number } | null>(null);
    const [selectionBox, setSelectionBox] = useState<{ x: number; y: number; width: number; height: number } | null>(
        null
    );
    const [isActive, setIsActive] = useState(false);
    const [hasPageBeenRendered, setHasPageBeenRendered] = useState(false);

    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    const handleOnMouseDown = (e: React.MouseEvent) => {
        if (selectionDisabled) {
            return;
        }
        if (!canvasRef.current) {
            setStartingPosition(null);
            setSelectionBox(null);
            return;
        }
        const canvasBoundingBox = canvasRef.current.getBoundingClientRect();
        const x = Math.max(e.clientX - canvasBoundingBox.left, 0);
        const y = Math.max(e.clientY - canvasBoundingBox.top, 0);

        setStartingPosition({ x, y });
        setIsActive(true);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!startingPosition || !canvasRef.current || !isActive) {
            return;
        }

        const canvasBoundingBox = canvasRef.current.getBoundingClientRect();
        const x = Math.max(e.clientX - canvasBoundingBox.left, 0);
        const y = Math.max(e.clientY - canvasBoundingBox.top, 0);

        const top = Math.min(startingPosition.y, y);
        const left = Math.min(startingPosition.x, x);
        const width = Math.abs(x - startingPosition.x);
        const height = Math.abs(y - startingPosition.y);
        setSelectionBox({
            x: left,
            y: top,
            width,
            height,
        });
    };

    const handleOnMouseUp = (e: React.MouseEvent) => {
        if (!isActive) {
            return;
        }
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');

        if (
            selectionBox &&
            selectionBox.width >= MIN_SELECTION_BOX_WIDTH_PX &&
            selectionBox.height >= MIN_SELECTION_BOX_HEIGHT_PX &&
            canvas &&
            context
        ) {
            const canvasBoundingBox = canvas.getBoundingClientRect();
            const x = pixelsToPercentage(selectionBox.x, canvasBoundingBox.width);
            const y = pixelsToPercentage(selectionBox.y, canvasBoundingBox.height);
            const width = pixelsToPercentage(selectionBox.width, canvasBoundingBox.width);
            const height = pixelsToPercentage(selectionBox.height, canvasBoundingBox.height);
            const xPX = percentageToPixels(x, canvas.width);
            const yPX = percentageToPixels(y, canvas.height);
            const widthPX = percentageToPixels(width, canvas.width);
            const heightPX = percentageToPixels(height, canvas.height);
            const extractionSpec: ExtractionSpecType = {
                page,
                id: uuidv4(),
                xPercentage: x,
                yPercentage: y,
                widthPercentage: width,
                heightPercentage: height,
                widthPX,
                heightPX,
                imageData: context.getImageData(xPX, yPX, widthPX, heightPX),
            };
            handleExtraction(extractionSpec);
        }
        setStartingPosition(null);
        setSelectionBox(null);
        setIsActive(false);
    };
    return (
        <>
            <div className="relative">
                <Page
                    renderTextLayer={true}
                    renderAnnotationLayer={false}
                    canvasRef={canvasRef}
                    onMouseDown={handleOnMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleOnMouseUp}
                    pageNumber={page}
                    scale={scale}
                    onLoadSuccess={onLoadSuccess}
                    onRenderSuccess={(page) => {
                        setHasPageBeenRendered(true);
                        if (onRenderSuccess) {
                            onRenderSuccess(page);
                        }
                    }}
                />
                {selectionBox && (
                    <SelectionBox
                        top={selectionBox?.y || 0}
                        left={selectionBox?.x || 0}
                        width={selectionBox?.width || 0}
                        height={selectionBox?.height || 0}
                    />
                )}
                {hasPageBeenRendered && (
                    <div className="selection-box-layer">
                        {pendingPageExtractionSpecs.map((pendingExtraction) => (
                            <PendingPageExtraction
                                key={pendingExtraction.id}
                                canvasBoundingBox={canvasRef.current?.getBoundingClientRect()}
                                extractionSpec={pendingExtraction}
                            />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}

type PendingPageExtractionPropsType = {
    canvasBoundingBox: DOMRect | undefined;
    extractionSpec: HighlightSpecType;
};
function PendingPageExtraction({ canvasBoundingBox, extractionSpec }: PendingPageExtractionPropsType) {
    if (!canvasBoundingBox) {
        return null;
    }
    const top = percentageToPixels(extractionSpec.yPercentage, canvasBoundingBox.height);
    const left = percentageToPixels(extractionSpec.xPercentage, canvasBoundingBox.width);
    const width = percentageToPixels(extractionSpec.widthPercentage, canvasBoundingBox.width);
    const height = percentageToPixels(extractionSpec.heightPercentage, canvasBoundingBox.height);
    return (
        <SelectionBox
            top={top}
            left={left}
            width={width}
            height={height}
            tooltip={extractionSpec.tooltip}
            className={extractionSpec.className}
        />
    );
}

type SelectionBoxPropsType = {
    top: number;
    left: number;
    width: number;
    height: number;
    // See HighlightSpecType.tooltip. Without it the box stays inert, which is what the extraction
    // flow and the live drag selection want.
    tooltip?: ReactNode;
    // See HighlightSpecType.className.
    className?: string;
};

function SelectionBox({ top, left, width, height, tooltip, className }: SelectionBoxPropsType) {
    const box = (
        <div
            className={`selection-box${tooltip ? ' selection-box--interactive' : ''}${
                className ? ` ${className}` : ''
            }`}
            style={{
                top,
                left,
                width,
                height,
            }}
        ></div>
    );

    if (!tooltip) {
        return box;
    }

    // Popup builds its content only while open, so an unopened highlight costs nothing, and it
    // portals to the body — which keeps the popup clear of the scrolling pane around the page.
    return <Popup on="click" className="provenance-details-popup" trigger={box} content={tooltip} />;
}

export default PDFPage;
